import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiMail, FiPhone, FiSend, FiEye, FiEyeOff, FiRefreshCw, FiTrash2, FiCheckCircle, FiAlertCircle, FiArrowLeft, FiList, FiActivity } from 'react-icons/fi';
import { showNotification } from './Notification';
import ProgressBar from './ProgressBar';
import ProviderStatusDashboard from './ProviderStatusDashboard';
import './Communication.css';

const Communication = () => {
  const [activeTab, setActiveTab] = useState('sms');
  const [activeView, setActiveView] = useState('compose'); // 'compose' or 'logs'
  const [logFilter, setLogFilter] = useState('delivered'); // 'delivered', 'failed', 'pending'
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    trainee_ids: []
  });
  const [sending, setSending] = useState(false);
  const [smsLogs, setSmsLogs] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [sendProgress, setSendProgress] = useState({ progress: 0, message: '', current: 0, total: 0 });
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [sendTarget, setSendTarget] = useState('all');
  const [traineeOptions, setTraineeOptions] = useState([]);
  const [traineeLoading, setTraineeLoading] = useState(false);
  const [traineeSearch, setTraineeSearch] = useState('');

  // Batch Size State
  const [batchSize, setBatchSize] = useState(20);
  const [startIndex, setStartIndex] = useState('');
  const [autoAdvance, setAutoAdvance] = useState(false);

  // Removed local balance state in favor of dashboard
  const [showDashboard, setShowDashboard] = useState(true);
  const [selectedLogIds, setSelectedLogIds] = useState([]);

  // Provider selection
  const [smsProvider, setSmsProvider] = useState('termii');
  const [emailProvider, setEmailProvider] = useState('sendgrid');

  // Confirm Modal State
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    action: null,
    type: 'delete'
  });

  useEffect(() => {
    setActiveView('compose'); // Reset to compose when switching tabs
    setSelectedLogIds([]);
    setLogFilter('delivered');
  }, [activeTab]);

  useEffect(() => {
    fetchLogs();
    fetchBatches();
    fetchBatches();
    // fetchBalance(); // Handled by dashboard now
  }, []);

  // Legacy balance fetch removed


  useEffect(() => {
    if (sendTarget === 'custom') {
      loadTraineeOptions(selectedBatchId);
      setFormData(prev => ({ ...prev, trainee_ids: [] }));
    }
  }, [selectedBatchId, sendTarget]);

  const fetchLogs = async () => {
    try {
      const [smsResponse, emailResponse] = await Promise.all([
        axios.get('/api/communication/sms/logs'),
        axios.get('/api/communication/email/logs')
      ]);
      setSmsLogs(Array.isArray(smsResponse.data?.logs) ? smsResponse.data.logs : []);
      setEmailLogs(Array.isArray(emailResponse.data?.logs) ? emailResponse.data.logs : []);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await axios.get('/api/batches');
      setBatches(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const loadTraineeOptions = async (batchIdValue = '') => {
    try {
      setTraineeLoading(true);
      const response = await axios.get('/api/trainees/list/simple', {
        params: { batch_id: batchIdValue || '' }
      });
      setTraineeOptions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching trainee options:', error);
      setTraineeOptions([]);
    } finally {
      setTraineeLoading(false);
    }
  };

  const executeSend = async (overrideOffset = null, currentStats = { sent: 0, failed: 0 }) => {
    try {
      setSending(true);
      setConfirmModal({ show: false, title: '', message: '', action: null });

      const currentOffset = overrideOffset !== null ? overrideOffset : (parseInt(startIndex) || 0);
      const effectiveBatchSize = parseInt(batchSize) || 20;

      setSendProgress(prev => ({
        ...prev,
        progress: 20,
        message: `Processing batch (Offset: ${currentOffset})...`
      }));

      const payloadBase = {
        message: formData.message.trim(),
        batch_size: effectiveBatchSize,
        start_index: currentOffset
      };

      if (activeTab === 'email') {
        payloadBase.subject = formData.subject.trim();
      }

      const sendRequest = (body) => {
        // Construct request
        const requestBody = {
          ...body,
          provider: activeTab === 'sms' ? smsProvider : emailProvider
        };
        const endpoint = activeTab === 'sms' ? '/api/communication/sms/bulk' : '/api/communication/email/bulk';
        return axios.post(endpoint, requestBody);
      };

      const requestBody = { ...payloadBase };
      if (sendTarget === 'custom') {
        requestBody.trainee_ids = formData.trainee_ids;
      } else if (sendTarget === 'batch' && selectedBatchId) {
        requestBody.batch_id = selectedBatchId;
      }

      const response = await sendRequest(requestBody);
      const batchSent = response.data.sent || 0;
      const batchFailed = response.data.errors || 0;
      const batchTotal = response.data.total || 0; // Items processed in this chunk

      const newStats = {
        sent: currentStats.sent + batchSent,
        failed: currentStats.failed + batchFailed
      };

      setSendProgress({
        progress: 100,
        message: `Batch result: ${batchSent} sent, ${batchFailed} errors.`,
        current: newStats.sent,
        total: batchTotal // Showing chunk total for now
      });

      // Determine if done: if fetched items < batchSize, we reached the end
      const isDone = batchTotal < effectiveBatchSize;

      if (isDone) {
        setTimeout(() => {
          setSending(false);
          setSendProgress({ progress: 0, message: '', current: 0, total: 0 });
          showNotification(`Operation Complete! Total: ${newStats.sent} sent, ${newStats.failed} failed.`, newStats.failed === 0 ? 'success' : 'warning');

          // Reset form
          setFormData({ subject: '', message: '', trainee_ids: [] });
          setTraineeSearch('');
          setSendTarget('all');
          setSelectedBatchId('');
          setStartIndex('');

          fetchLogs();
          fetchBatches();
          fetchLogs();
          fetchBatches();
          // fetchBalance();
        }, 1500);
      } else {
        // More to process
        const nextOffset = currentOffset + effectiveBatchSize;

        if (autoAdvance) {
          setSendProgress(prev => ({ ...prev, message: `Auto-advancing to next batch (Offset ${nextOffset})...` }));
          setTimeout(() => {
            executeSend(nextOffset, newStats);
          }, 2000);
        } else {
          setSending(false); // Pause UI to allow interaction
          setConfirmModal({
            show: true,
            title: 'Batch Completed - Proceed?',
            message: (
              <div>
                <p><strong>This Batch:</strong> {batchSent} Sent, {batchFailed} Failed</p>
                <p><strong>Total Session:</strong> {newStats.sent} Sent, {newStats.failed} Failed</p>
                <p className="mt-2 text-info">More records remain. Continue to next batch?</p>
                <div className="mt-3 p-2 bg-light rounded">
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={autoAdvance}
                      onChange={(e) => setAutoAdvance(e.target.checked)}
                      style={{ marginRight: '10px' }}
                    />
                    Auto-advance remaining batches (Don't ask again)
                  </label>
                </div>
              </div>
            ),
            action: () => executeSend(nextOffset, newStats),
            type: 'retry'
          });
        }
      }

    } catch (error) {
      setSending(false);
      setSendProgress({ progress: 0, message: '', current: 0, total: 0 });
      showNotification(error.response?.data?.error || 'Error sending messages', 'error');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();

    if (sendTarget === 'batch' && !selectedBatchId) {
      showNotification('Please select a batch', 'error');
      return;
    }
    if (sendTarget === 'custom' && formData.trainee_ids.length === 0) {
      showNotification('Please select at least one recipient', 'error');
      return;
    }
    if (!formData.message.trim()) {
      showNotification('Message content is required', 'error');
      return;
    }
    if (activeTab === 'email' && !formData.subject.trim()) {
      showNotification('Subject is required for emails', 'error');
      return;
    }

    // Calculate recipient info
    let recipientDescription = '';
    const provName = activeTab === 'sms'
      ? (smsProvider === 'termii' ? 'Termii' : 'Twilio')
      : (emailProvider === 'sendgrid' ? 'SendGrid' : (emailProvider === 'termii' ? 'Termii' : 'Resend'));

    if (sendTarget === 'custom') {
      recipientDescription = `${formData.trainee_ids.length} selected recipients`;
    } else if (sendTarget === 'batch') {
      const batchName = batches.find(b => String(b.id) === String(selectedBatchId))?.name || 'Selected Batch';
      recipientDescription = `trainees in batch "${batchName}"`;
    } else {
      recipientDescription = 'all filtered trainees';
    }

    setConfirmModal({
      show: true,
      title: 'Initialize Send',
      message: (
        <>
          <p><strong>Target:</strong> {recipientDescription}</p>
          <p><strong>Provider:</strong> {provName}</p>
          <p><strong>Batch Size:</strong> {batchSize} per request</p>
          <div className="mt-3 p-2 bg-light rounded">
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0 }}>
              <input
                type="checkbox"
                checked={autoAdvance}
                onChange={(e) => setAutoAdvance(e.target.checked)}
                style={{ marginRight: '10px' }}
              />
              Auto-advance batches (No confirmation between chunks)
            </label>
          </div>
        </>
      ),
      action: () => executeSend(null, { sent: 0, failed: 0 }),
      type: 'retry'
    });
  };

  const handleRetry = async (log, type) => {
    try {
      setSending(true);
      const recipientName = `${log.first_name || ''} ${log.last_name || ''}`.trim() || 'Trainee';

      const payload = {
        trainee_ids: [log.trainee_id],
        message: log.message,
        subject: type === 'email' ? log.subject : undefined,
        provider: type === 'sms' ? smsProvider : emailProvider,
        batch_size: 1,
        retry_ids: { [log.trainee_id]: log.id }
      };

      const endpoint = type === 'sms' ? '/api/communication/sms/bulk' : '/api/communication/email/bulk';
      await axios.post(endpoint, payload);

      showNotification(`Retry sent successfully`, 'success');
      fetchLogs();
      fetchLogs();
      // fetchBalance();
    } catch (error) {
      console.error('Retry error:', error);
      showNotification(error.response?.data?.error || 'Failed to retry message.', 'error');
    } finally {
      setSending(false);
    }
  };

  const toggleLogSelection = (id) => {
    setSelectedLogIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const getFilteredLogs = () => {
    const currentLogs = activeTab === 'sms' ? smsLogs : emailLogs;
    if (logFilter === 'delivered') return currentLogs.filter(log => log.status === 'sent' || log.status.startsWith('sent'));
    if (logFilter === 'failed') return currentLogs.filter(log => log.status && log.status.startsWith('failed'));
    if (logFilter === 'pending') return currentLogs.filter(log => log.status === 'pending');
    return currentLogs;
  };

  const toggleAllLogs = () => {
    const logs = getFilteredLogs();
    if (selectedLogIds.length === logs.length && logs.length > 0) {
      setSelectedLogIds([]);
    } else {
      setSelectedLogIds(logs.map(log => log.id));
    }
  };

  const handleBulkDelete = () => {
    setConfirmModal({
      show: true,
      title: 'Delete Logs',
      message: `Are you sure you want to delete ${selectedLogIds.length} logs? This action cannot be undone.`,
      action: executeBulkDelete,
      type: 'delete'
    });
  };

  const executeBulkDelete = async () => {
    try {
      setSending(true);
      await axios.delete('/api/communication/logs', {
        data: { ids: selectedLogIds, type: activeTab }
      });
      showNotification(`Successfully deleted ${selectedLogIds.length} logs`, 'success');
      setSelectedLogIds([]);
      setConfirmModal({ show: false, title: '', message: '', action: null });
      fetchLogs();
    } catch (error) {
      console.error('Bulk delete error:', error);
      showNotification('Failed to delete logs', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleBulkRetry = async () => {
    setConfirmModal({
      show: true,
      title: 'Resend Selected',
      message: `Are you sure you want to resend to these ${selectedLogIds.length} recipients?`,
      action: executeBulkRetry,
      type: 'retry'
    });
  };

  const executeBulkRetry = async () => {
    try {
      setSending(true);
      const currentLogs = activeTab === 'sms' ? smsLogs : emailLogs;
      const logsToRetry = currentLogs.filter(log => selectedLogIds.includes(log.id));

      let successCount = 0;
      let failCount = 0;
      const endpoint = activeTab === 'sms' ? '/api/communication/sms/bulk' : '/api/communication/email/bulk';

      const groups = {};
      logsToRetry.forEach(log => {
        const key = JSON.stringify({ msg: log.message, subj: log.subject });
        if (!groups[key]) groups[key] = [];
        groups[key].push({ trainee_id: log.trainee_id, log_id: log.id });
      });

      for (const key of Object.keys(groups)) {
        const { msg, subj } = JSON.parse(key);
        const items = groups[key];
        const traineeIds = items.map(i => i.trainee_id);
        const retryIds = items.reduce((acc, curr) => ({ ...acc, [curr.trainee_id]: curr.log_id }), {});

        try {
          await axios.post(endpoint, {
            trainee_ids: traineeIds,
            message: msg,
            subject: subj,
            batch_size: batchSize,
            retry_ids: retryIds
          });
          successCount += traineeIds.length;
        } catch (err) {
          console.error('Batch retry error:', err);
          failCount += traineeIds.length;
        }
      }

      showNotification(`Bulk retry complete: ${successCount} sent, ${failCount} failed`, failCount === 0 ? 'success' : 'warning');
      setSelectedLogIds([]);
      setConfirmModal({ show: false, title: '', message: '', action: null });
      fetchLogs();
    } catch (error) {
      console.error('Bulk retry error:', error);
      showNotification('Failed to process bulk retry', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleRetryAllFailed = async () => {
    const currentLogs = activeTab === 'sms' ? smsLogs : emailLogs;
    const failedLogs = currentLogs.filter(log => log.status && log.status.startsWith('failed'));

    if (failedLogs.length === 0) {
      showNotification('No failed logs to retry', 'info');
      return;
    }

    const failedIds = failedLogs.map(l => l.id);
    setSelectedLogIds(failedIds);

    setConfirmModal({
      show: true,
      title: 'Resend All Failed',
      message: `Found ${failedLogs.length} failed messages. Resend all of them now?`,
      action: executeBulkRetry,
      type: 'retry'
    });
  };

  const filteredTrainees = Array.isArray(traineeOptions)
    ? traineeOptions.filter(trainee => {
      if (!traineeSearch) return true;
      const term = traineeSearch.toLowerCase();
      return (
        `${trainee.first_name || ''} ${trainee.last_name || ''}`.toLowerCase().includes(term) ||
        (trainee.tag_number || '').toLowerCase().includes(term) ||
        (trainee.serial_number || '').toLowerCase().includes(term) ||
        (trainee.email || '').toLowerCase().includes(term) ||
        (trainee.phone_number || '').includes(term)
      );
    })
    : [];

  const toggleTraineeSelection = (id) => {
    setFormData(prev => {
      const exists = prev.trainee_ids.includes(id);
      return {
        ...prev,
        trainee_ids: exists
          ? prev.trainee_ids.filter(item => item !== id)
          : [...prev.trainee_ids, id]
      };
    });
  };

  const selectAllFiltered = () => {
    setFormData(prev => ({
      ...prev,
      trainee_ids: filteredTrainees.map(t => t.id)
    }));
  };

  const clearSelectedTrainees = () => {
    setFormData(prev => ({ ...prev, trainee_ids: [] }));
  };

  const resolveTargetIds = async () => {
    if (sendTarget === 'custom') {
      return formData.trainee_ids;
    }
    if (sendTarget === 'batch' && !selectedBatchId) {
      return [];
    }
    const batchParam = sendTarget === 'batch' ? selectedBatchId : '';
    const response = await axios.get('/api/trainees/list/simple', {
      params: { batch_id: batchParam || '' }
    });
    return Array.isArray(response.data) ? response.data.map(t => t.id) : [];
  };

  const currentFilteredLogs = getFilteredLogs();

  return (
    <div className="communication">
      <div className="communication-header">
        <div className="header-title">
          <h1>Communication</h1>
          <h1>Communication</h1>
          <button
            className={`btn btn-sm ${showDashboard ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setShowDashboard(!showDashboard)}
          >
            <FiActivity style={{ marginRight: '5px' }} />
            {showDashboard ? 'Hide Status' : 'Show System Status'}
          </button>
        </div>

        {activeView === 'compose' && (
          <button className="btn btn-secondary" onClick={() => setActiveView('logs')}>
            <FiList /> View Logs
          </button>
        )}
      </div>

      {/* Provider Status Dashboard */}
      {showDashboard && <ProviderStatusDashboard />}

      {/* Main Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'sms' ? 'active' : ''}`}
          onClick={() => setActiveTab('sms')}
        >
          <FiPhone /> SMS
        </button>
        <button
          className={`tab ${activeTab === 'email' ? 'active' : ''}`}
          onClick={() => setActiveTab('email')}
        >
          <FiMail /> Email
        </button>
      </div>

      {/* COMPOSE VIEW */}
      {activeView === 'compose' && (
        <div className="compose-container">
          {sendProgress.progress > 0 && (
            <ProgressBar
              progress={sendProgress.progress}
              message={sendProgress.message}
              variant="circular"
              current={sendProgress.current}
              total={sendProgress.total}
            />
          )}

          <form onSubmit={handleSend}>
            <div className="form-row">
              <div className="form-group half">
                <label>{activeTab === 'sms' ? 'SMS Provider' : 'Email Provider'}</label>
                <select
                  value={activeTab === 'sms' ? smsProvider : emailProvider}
                  onChange={(e) => activeTab === 'sms' ? setSmsProvider(e.target.value) : setEmailProvider(e.target.value)}
                  className="form-select"
                >
                  {activeTab === 'sms' ? (
                    <>
                      <option value="termii">Termii (Nigerian SMS)</option>
                      <option value="twilio">Twilio (Global SMS)</option>
                    </>
                  ) : (
                    <>
                      <option value="sendgrid">SendGrid (Professional Email)</option>
                      <option value="resend">Resend (High Deliverability)</option>
                      <option value="termii">Termii (Nigerian Email)</option>
                    </>
                  )}
                </select>
              </div>
              <div className="form-group half">
                <label>Batch Size (Speed)</label>
                <select
                  value={batchSize}
                  onChange={(e) => setBatchSize(parseInt(e.target.value))}
                  className="form-select"
                >
                  <option value="5">5 per batch (Slow/Safe)</option>
                  <option value="20">20 per batch (Normal)</option>
                  <option value="50">50 per batch (Fast)</option>
                  <option value="100">100 per batch (Turbo)</option>
                </select>
              </div>
            </div>

            {activeTab === 'email' && (
              <div className="form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  placeholder="Email subject"
                  className="form-control"
                />
              </div>
            )}

            <div className="form-group">
              <label>Message *</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows="6"
                placeholder={activeTab === 'sms' ? 'SMS message (160 characters recommended)' : 'Email message. Use {name} for personalization.'}
                className="form-control"
              />
            </div>

            <div className="form-section-title">Recipient Selection</div>

            <div className="form-group">
              <label>Filter by Batch</label>
              <select
                value={selectedBatchId}
                onChange={(e) => setSelectedBatchId(e.target.value)}
                className="form-select"
              >
                <option value="">All Batches</option>
                {Array.isArray(batches) && batches.map(batch => (
                  <option key={batch.id} value={batch.id}>
                    {batch.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <div className="target-options">
                <label>
                  <input
                    type="radio"
                    value="all"
                    checked={sendTarget === 'all'}
                    onChange={(e) => setSendTarget(e.target.value)}
                  />
                  <span>All filtered</span>
                </label>
                <label>
                  <input
                    type="radio"
                    value="batch"
                    checked={sendTarget === 'batch'}
                    onChange={(e) => setSendTarget(e.target.value)}
                  />
                  <span>Only selected batch</span>
                </label>
                <label>
                  <input
                    type="radio"
                    value="custom"
                    checked={sendTarget === 'custom'}
                    onChange={(e) => setSendTarget(e.target.value)}
                  />
                  <span>Manual select</span>
                </label>
              </div>
            </div>

            {sendTarget === 'custom' && (
              <div className="trainee-selector">
                <div className="selector-header">
                  <input
                    type="text"
                    placeholder="Search by name, SN/Tag..."
                    value={traineeSearch}
                    onChange={(e) => setTraineeSearch(e.target.value)}
                  />
                  <div className="selector-actions">
                    <button type="button" onClick={selectAllFiltered} disabled={traineeLoading || filteredTrainees.length === 0}>
                      Select All ({filteredTrainees.length})
                    </button>
                    <button type="button" onClick={clearSelectedTrainees} disabled={formData.trainee_ids.length === 0}>
                      Clear
                    </button>
                  </div>
                </div>

                <div className="selector-list">
                  {traineeLoading ? (
                    <div className="loading-text">Loading trainees...</div>
                  ) : filteredTrainees.length === 0 ? (
                    <div className="empty-text">No trainees found</div>
                  ) : (
                    filteredTrainees.map(trainee => (
                      <div
                        key={trainee.id}
                        className={`selector-item ${formData.trainee_ids.includes(trainee.id) ? 'selected' : ''}`}
                        onClick={() => toggleTraineeSelection(trainee.id)}
                      >
                        <input
                          type="checkbox"
                          checked={formData.trainee_ids.includes(trainee.id)}
                          onChange={() => { }}
                        />
                        <div>
                          <strong>{trainee.first_name} {trainee.last_name}</strong>
                          <small>{trainee.tag_number || trainee.serial_number} • {activeTab === 'sms' ? trainee.phone_number : trainee.email}</small>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="selector-footer">
                  {formData.trainee_ids.length} selected
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary btn-lg" disabled={sending}>
                <FiSend /> {sending ? 'Sending...' : `Send ${activeTab === 'sms' ? 'SMS' : 'Email'}`}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* LOGS VIEW */}
      {activeView === 'logs' && (
        <div className="logs-view">
          <div className="logs-header">
            <button className="btn btn-secondary" onClick={() => setActiveView('compose')}>
              <FiArrowLeft /> Back to Compose
            </button>
          </div>

          {/* Sub-tabs for Delivered/Failed/Pending */}
          <div className="log-subtabs">
            <button
              className={`subtab ${logFilter === 'delivered' ? 'active' : ''}`}
              onClick={() => setLogFilter('delivered')}
            >
              <FiCheckCircle className="icon-sm" /> Delivered
            </button>
            <button
              className={`subtab ${logFilter === 'failed' ? 'active' : ''}`}
              onClick={() => setLogFilter('failed')}
            >
              <FiAlertCircle className="icon-sm" /> Failed
            </button>
            <button
              className={`subtab ${logFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setLogFilter('pending')}
            >
              <FiRefreshCw className="icon-sm" /> Pending
            </button>
          </div>

          {/* Log Filters - NOW REMOVED, USING SUB-TABS INSTEAD */}
          {false && (
            <div className="log-filters">
              <button
                className={`filter-btn ${logFilter === 'all' ? 'active' : ''}`}
                onClick={() => setLogFilter('all')}
              >
                All Logs
              </button>
              <button
                className={`filter-btn ${logFilter === 'delivered' ? 'active' : ''}`}
                onClick={() => setLogFilter('delivered')}
              >
                <FiCheckCircle className="icon-sm" /> Delivered
              </button>
              <button
                className={`filter-btn ${logFilter === 'failed' ? 'active' : ''}`}
                onClick={() => setLogFilter('failed')}
              >
                <FiAlertCircle className="icon-sm" /> Failed
              </button>
            </div>
          )}

          {currentFilteredLogs.length > 0 && selectedLogIds.length > 0 && (
            <div className="bulk-actions-bar">
              <div className="selected-count">{selectedLogIds.length} selected</div>
              <div className="bulk-actions">
                <button className="btn btn-secondary btn-sm" onClick={handleBulkDelete} disabled={sending}>
                  <FiTrash2 /> Delete
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleBulkRetry} disabled={sending}>
                  <FiRefreshCw /> Resend Selected
                </button>
              </div>
            </div>
          )}

          {/* "Retry All Failed" Shortcut */}
          {logFilter === 'failed' && currentFilteredLogs.length > 0 && selectedLogIds.length === 0 && (
            <div className="bulk-actions-bar info-bar">
              <span>Found {currentFilteredLogs.length} failed messages.</span>
              <button className="btn btn-primary btn-sm" onClick={handleRetryAllFailed} disabled={sending}>
                <FiRefreshCw /> Resend All Failed
              </button>
            </div>
          )}

          <div className="logs-container">
            <div className="logs-table">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={currentFilteredLogs.length > 0 && selectedLogIds.length === currentFilteredLogs.length}
                        onChange={toggleAllLogs}
                        disabled={currentFilteredLogs.length === 0}
                      />
                    </th>
                    <th>Tag/Name</th>
                    <th>{activeTab === 'sms' ? 'Phone' : 'Email'}</th>
                    {activeTab === 'email' && <th>Subject</th>}
                    <th>Message/Error</th>
                    <th>Status</th>
                    <th>Sent At</th>
                  </tr>
                </thead>
                <tbody>
                  {!Array.isArray(currentFilteredLogs) || currentFilteredLogs.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
                        {logFilter === 'failed' ? 'Great! No failed messages found.' : 'No logs found.'}
                      </td>
                    </tr>
                  ) : (
                    currentFilteredLogs.map(log => (
                      <tr key={log.id} className={selectedLogIds.includes(log.id) ? 'selected-row' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedLogIds.includes(log.id)}
                            onChange={() => toggleLogSelection(log.id)}
                          />
                        </td>
                        <td>
                          <div className="log-user-info">
                            <strong>{log.tag_number || '-'}</strong>
                            <span>{log.first_name} {log.last_name}</span>
                          </div>
                        </td>
                        <td>{activeTab === 'sms' ? log.phone_number : log.email_address}</td>
                        {activeTab === 'email' && <td>{log.subject}</td>}
                        <td className="message-cell">
                          {log.status.startsWith('failed') ? (
                            <span className="error-reason" title={log.status}>
                              {log.status.replace('failed:', '').trim()}
                            </span>
                          ) : (
                            <span title={log.message}>{log.message.substring(0, 40)}...</span>
                          )}
                        </td>
                        <td>
                          <div className="status-cell">
                            <span className={`status-badge ${log.status.startsWith('failed') ? 'failed' : log.status.startsWith('sent') ? 'sent' : log.status}`}>
                              {log.status.startsWith('failed') ? 'Failed' : log.status}
                            </span>
                            {log.status.startsWith('failed') && (
                              <button
                                className="retry-btn"
                                onClick={() => handleRetry(log, activeTab)}
                                title="Retry this single message"
                                disabled={sending}
                              >
                                <FiRefreshCw />
                              </button>
                            )}
                          </div>
                        </td>
                        <td>{log.sent_at ? new Date(log.sent_at).toLocaleString() : '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRMATION MODAL */}
      {confirmModal.show && (
        <div className="modal" onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{confirmModal.title}</h2>
            </div>
            <div className="modal-body">
              {confirmModal.message}
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))}
                disabled={sending}
              >
                Cancel
              </button>
              <button
                className={`btn ${confirmModal.type === 'delete' ? 'btn-danger' : 'btn-primary'}`}
                onClick={confirmModal.action}
                disabled={sending}
              >
                {sending ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Communication;
